import * as signalR from '@microsoft/signalr';
import { getCookie } from '../utils/cookie';
import { getPublicDealerIdFromParams } from '../helpers/params/params';

export type UUID = string & { readonly __brand: unique symbol };

export interface LeaseContractEvent {
  applicationId: string;
  orderId: string;
  leaseTerm?: string;
  selectedBy?: UUID;
  timestamp: string;
}

export interface LeaseContractSignedEvent {
  applicationId: string;
  selectedBy?: UUID;
  timestamp: string;
}

export type ConnectionStateChangeCallback = (state: signalR.HubConnectionState, error?: Error) => void;

class SignalRService {
  private connection: signalR.HubConnection | null = null;
  private applicationId: string | null = null;
  private subscriberCount = 0;
  private pendingDisconnect: Promise<void> | null = null;
  private connectionStateChangeCallback: ConnectionStateChangeCallback | null = null;
  private browserTabConcurrencyId: UUID = crypto.randomUUID() as UUID;

  subscribe(): void {
    this.subscriberCount++;
    console.log('[SignalR] subscribe, count:', this.subscriberCount);
  }

  unsubscribe(): void {
    this.subscriberCount = Math.max(0, this.subscriberCount - 1);
    console.log('[SignalR] unsubscribe, count:', this.subscriberCount);
  }

  getSubscriberCount(): number {
    return this.subscriberCount;
  }

  setConnectionStateChangeCallback(callback: ConnectionStateChangeCallback | null): void {
    this.connectionStateChangeCallback = callback;
  }

  private notifyStateChange(error?: Error): void {
    if (this.connectionStateChangeCallback && this.connection) {
      this.connectionStateChangeCallback(this.connection.state, error);
    }
  }

  private getPublicDealerId(): string | null {
    return getPublicDealerIdFromParams() || sessionStorage.getItem('publicDealerId');
  }

  private buildConnection(baseUrl: string): signalR.HubConnection {
    const publicDealerId = this.getPublicDealerId();
    const url = new URL(`${baseUrl}/signalr`);
    if (publicDealerId) {
      url.searchParams.set('publicDealerId', publicDealerId);
    }

    const headers: Record<string, string> = {};
    const unifiedToken = getCookie('unifiedApplicationToken');
    if (unifiedToken) {
      headers['Cookie'] = `unifiedApplicationToken=${unifiedToken}`;
    }
    if (publicDealerId) {
      headers['public-dealer-id'] = publicDealerId;
    }

    return new signalR.HubConnectionBuilder()
      .withUrl(url.toString(), {
        transport: signalR.HttpTransportType.ServerSentEvents,
        headers,
        withCredentials: true,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Information)
      .withServerTimeout(30000)
      .withKeepAliveInterval(15000)
      .build();
  }

  async connect(baseUrl: string): Promise<void> {
    console.log('[SignalR] connect called', { subscriberCount: this.subscriberCount, state: this.connection?.state });

    if (this.pendingDisconnect) {
      console.log('[SignalR] Waiting for pending disconnect');
      await this.pendingDisconnect;
      this.pendingDisconnect = null;
    }

    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      console.log('[SignalR] Already connected');
      return;
    }

    if (this.connection?.state === signalR.HubConnectionState.Disconnected) {
      this.connection = null;
    }

    if (!this.connection) {
      this.connection = this.buildConnection(baseUrl);

      this.connection.onclose((error) => {
        console.log('[SignalR] Connection closed', error);
        this.notifyStateChange(error);
      });

      this.connection.onreconnecting((error) => {
        console.log('[SignalR] Reconnecting', error);
        this.notifyStateChange(error);
      });

      this.connection.onreconnected(() => {
        console.log('[SignalR] Reconnected');
        this.notifyStateChange();
        if (this.applicationId) {
          this.joinApplicationGroup(this.applicationId);
        }
      });

      this.connection.on('leaseContractGenerated', (event: LeaseContractEvent) => {
        console.log('[SignalR] leaseContractGenerated:', event);
        this.onLeaseContractGenerated(event);
      });

      this.connection.on('leaseContractSigned', (event: LeaseContractSignedEvent) => {
        console.log('[SignalR] leaseContractSigned:', event);
        this.onLeaseContractSigned(event);
      });
    }

    await this.connection.start();
    console.log('[SignalR] Connected');
    this.notifyStateChange();

    if (this.applicationId) {
      await this.joinApplicationGroup(this.applicationId);
    }
  }

  async disconnect(): Promise<void> {
    console.log('[SignalR] disconnect called', { subscriberCount: this.subscriberCount });

    if (this.subscriberCount > 0) {
      console.log('[SignalR] Still have subscribers, skipping');
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, 1000));

    if (this.subscriberCount > 0) {
      console.log('[SignalR] New subscribers appeared during debounce, skipping disconnect');
      return;
    }

    this.pendingDisconnect = this.doDisconnect();
    await this.pendingDisconnect;
    this.pendingDisconnect = null;
  }

  private async doDisconnect(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.stop();
      console.log('[SignalR] Disconnected');
    }
    this.connection = null;
    this.applicationId = null;
  }

  async joinApplicationGroup(applicationId: string): Promise<void> {
    if (this.connection?.state !== signalR.HubConnectionState.Connected) {
      console.warn('[SignalR] Cannot join group - not connected');
      return;
    }

    await this.connection.invoke('joinApplicationGroup', applicationId);
    this.applicationId = applicationId;
    console.log('[SignalR] Joined group:', applicationId);
  }

  async leaveApplicationGroup(applicationId: string): Promise<void> {
    if (this.connection?.state !== signalR.HubConnectionState.Connected) {
      return;
    }

    try {
      await this.connection.invoke('leaveApplicationGroup', applicationId);
      console.log('[SignalR] Left group:', applicationId);
    } catch (error) {
      console.error('[SignalR] Failed to leave group', error);
    }
  }

  onLeaseContractGenerated(_event: LeaseContractEvent): void {}
  onLeaseContractSigned(_event: LeaseContractSignedEvent): void {}

  isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }

  getConnectionState(): signalR.HubConnectionState | null {
    return this.connection?.state ?? null;
  }

  getBrowserTabConcurrencyId(): UUID {
    return this.browserTabConcurrencyId;
  }
}

export const signalRService = new SignalRService();
